import type { InventoryItem } from "./api";

export interface PosCartLine {
  item: InventoryItem;
  quantity: number;
  unitPriceMinor: number;
  lineTotalMinor: number;
}

const safeWholeNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 0;
};

export function buildPosCart(
  inventory: InventoryItem[],
  quantities: Record<string, number>,
): PosCartLine[] {
  return inventory.flatMap((item) => {
    const quantity = safeWholeNumber(quantities[item.code]);
    if (quantity === 0) return [];

    const unitPriceMinor = safeWholeNumber(item.priceMinor);
    return [{ item, quantity, unitPriceMinor, lineTotalMinor: unitPriceMinor * quantity }];
  });
}

export function posCartItemCount(lines: PosCartLine[]) {
  return lines.reduce((total, line) => total + line.quantity, 0);
}

export function posCartSubtotalMinor(lines: PosCartLine[]) {
  return lines.reduce((total, line) => total + line.lineTotalMinor, 0);
}

interface PosDiscountInput {
  grossAmountMinor: number;
  couponDiscountMinor: number;
  points: null | {
    enabled: boolean;
    requestedPoints: number;
    maximumRedeemablePoints: number;
    pointsPerUnit: number;
    valuePerUnitMinor: number;
  };
  credits: null | {
    enabled: boolean;
    requestedMinor: number;
    maximumRedeemableMinor: number;
  };
}

export interface PosDiscountTotals {
  couponDiscountMinor: number;
  maximumApplicableRewardPoints: number;
  pointDiscountMinor: number;
  maximumApplicableCreditsMinor: number;
  appliedMeroCreditsMinor: number;
  customerPaysMinor: number;
}

export function calculatePosDiscounts(input: PosDiscountInput): PosDiscountTotals {
  const grossAmountMinor = safeWholeNumber(input.grossAmountMinor);
  const couponDiscountMinor = Math.min(
    grossAmountMinor,
    safeWholeNumber(input.couponDiscountMinor),
  );
  const amountAfterCouponMinor = grossAmountMinor - couponDiscountMinor;

  const pointsPerUnit = Math.max(1, safeWholeNumber(input.points?.pointsPerUnit));
  const valuePerUnitMinor = Math.max(1, safeWholeNumber(input.points?.valuePerUnitMinor));
  const maximumApplicableRewardPoints = input.points
    ? Math.floor(
        Math.min(
          safeWholeNumber(input.points.maximumRedeemablePoints),
          Math.floor(amountAfterCouponMinor / valuePerUnitMinor) * pointsPerUnit,
        ) / pointsPerUnit,
      ) * pointsPerUnit
    : 0;
  const appliedRewardPoints = input.points?.enabled
    ? Math.min(safeWholeNumber(input.points.requestedPoints), maximumApplicableRewardPoints)
    : 0;
  const pointDiscountMinor =
    Math.floor(appliedRewardPoints / pointsPerUnit) * valuePerUnitMinor;

  const amountAfterPointsMinor = Math.max(
    0,
    amountAfterCouponMinor - pointDiscountMinor,
  );
  const maximumApplicableCreditsMinor = input.credits
    ? Math.min(
        safeWholeNumber(input.credits.maximumRedeemableMinor),
        amountAfterPointsMinor,
      )
    : 0;
  const appliedMeroCreditsMinor = input.credits?.enabled
    ? Math.min(safeWholeNumber(input.credits.requestedMinor), maximumApplicableCreditsMinor)
    : 0;

  return {
    couponDiscountMinor,
    maximumApplicableRewardPoints,
    pointDiscountMinor,
    maximumApplicableCreditsMinor,
    appliedMeroCreditsMinor,
    customerPaysMinor: Math.max(0, amountAfterPointsMinor - appliedMeroCreditsMinor),
  };
}
